import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {
  api,
  downloadSuperPickerChatReport,
  sendSuperPickerChatFeedback,
  sendSuperPickerChatMessage,
  transcribeSuperPickerChatAudio,
  SuperPickerChatHistoryTurn,
  SuperPickerChatProjectCard,
  SuperPickerChatRecommendationCard,
  SuperPickerChatResults,
  SuperPickerChatUnitCard,
} from '../../services/api';
import { logSuperPickerChatFeedback } from '../../services/analytics';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  /** DB id of the assistant message — target of thumbs feedback. */
  messageId?: number;
  /** Tool labels for assistant turns (e.g. "Searched projects"). */
  toolSummary?: string[];
  /** Card data for assistant turns. */
  results?: SuperPickerChatResults;
  /** Feedback already recorded for this turn. */
  feedback?: 'up' | 'down';
}

/** One-tap 👎 reasons (plan Phase 3): satisfaction signal, not accuracy. */
const DOWN_REASONS = [
  'Wrong price/availability',
  'Wrong location',
  "Didn't understand me",
  'Outdated info',
];

/** Auto-stop push-to-talk recordings after this long. */
const MAX_RECORDING_SECONDS = 60;

/** First MediaRecorder container the browser supports (iOS Safari → mp4). */
function pickRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read recording'));
    reader.readAsDataURL(blob);
  });

/** Strip markdown-ish syntax so speechSynthesis doesn't read asterisks aloud. */
const toSpeakableText = (text: string): string =>
  text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__|\*|_|`{1,3}|#{1,6}\s)/g, '')
    .replace(/\s+/g, ' ')
    .trim();

interface ImageViewerState {
  title: string;
  imageUrl: string | null;
  loading: boolean;
  error: string | null;
}

const formatPrice = (value: number | string | null | undefined): string => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 'Price on request';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
  return `$${Math.round(num)}`;
};

const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  p: 1.25,
  bgcolor: 'background.paper',
} as const;

const cardActionSx = {
  textTransform: 'none',
  fontSize: 12,
  px: 1,
  py: 0.25,
  minWidth: 0,
  color: 'var(--color-accent)',
} as const;

function ProjectCard({
  project,
  onViewSitePlan,
  onOpenProject,
}: {
  project: SuperPickerChatProjectCard;
  onViewSitePlan: (projectId: string, projectName: string) => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const bedroomLabels: Record<string, string> = { br1: '1BR', br2: '2BR', br3: '3BR', br4: '4BR', br5: '5BR', ph: 'PH' };
  const fromLines = Object.entries(project.availableByBedroom ?? {})
    .filter(([, stats]) => stats && stats.availableUnits > 0 && stats.fromPrice)
    .slice(0, 3)
    .map(([key, stats]) => `${bedroomLabels[key] ?? key} fr ${formatPrice(stats!.fromPrice)}`);
  const nearest = project.nearbyAmenities?.nearest?.[0];

  return (
    <Box sx={cardSx}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        {project.name}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
        {project.district && <Chip label={project.district} size="small" />}
        {project.region && <Chip label={project.region} size="small" />}
        {project.topYear ? <Chip label={`TOP ${project.topYear}`} size="small" /> : null}
        {project.distanceKm != null && <Chip label={`${project.distanceKm} km away`} size="small" variant="outlined" />}
      </Stack>
      {fromLines.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          {fromLines.join(' · ')}
        </Typography>
      )}
      {project.nearestMrt && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {project.nearestMrt}
          {project.walkingTimeToMrtMin != null ? ` · ${project.walkingTimeToMrtMin} min walk` : ''}
        </Typography>
      )}
      {nearest && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {nearest.name} · {nearest.distanceMeters}m
          {(project.nearbyAmenities?.count ?? 0) > 1 ? ` (+${project.nearbyAmenities!.count - 1} more)` : ''}
        </Typography>
      )}
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
        <Button size="small" startIcon={<MapOutlinedIcon />} sx={cardActionSx}
          onClick={() => onViewSitePlan(project.projectId, project.name)}>
          Site plan
        </Button>
        {onOpenProject && (
          <Button size="small" startIcon={<OpenInNewIcon />} sx={cardActionSx}
            onClick={() => onOpenProject(project.projectId)}>
            Open project
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function UnitCard({
  unit,
  onViewFloorPlan,
  onOpenProject,
}: {
  unit: SuperPickerChatUnitCard;
  onViewFloorPlan: (url: string, title: string) => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const specs = [
    unit.bedroom != null && unit.bedroom !== 0 ? `${unit.bedroom}BR` : null,
    unit.areaSqft ? `${Math.round(Number(unit.areaSqft))} sqft` : null,
    unit.psf ? `$${Math.round(Number(unit.psf))} psf` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={cardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {unit.projectName} · {unit.unitNumber}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatPrice(unit.price)}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {specs && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{specs}</Typography>
        )}
        {unit.discountPercent ? (
          <Chip label={`↓${unit.discountPercent}%`} size="small" color="warning" variant="outlined" />
        ) : null}
        {unit.betterThanCount ? (
          <Chip label={`Beats ${unit.betterThanCount} pricier units`} size="small" color="success" variant="outlined" />
        ) : null}
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        {unit.floorPlanUrl && (
          <Button size="small" startIcon={<ArchitectureOutlinedIcon />} sx={cardActionSx}
            onClick={() => onViewFloorPlan(unit.floorPlanUrl!, `${unit.projectName} ${unit.unitNumber} — Floor plan`)}>
            Floor plan
          </Button>
        )}
        {onOpenProject && unit.projectId && (
          <Button size="small" startIcon={<OpenInNewIcon />} sx={cardActionSx}
            onClick={() => onOpenProject(unit.projectId!)}>
            Open project
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function RecommendationCard({
  rec,
  onViewFloorPlan,
  onOpenProject,
}: {
  rec: SuperPickerChatRecommendationCard;
  onViewFloorPlan: (url: string, title: string) => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const specs = [
    rec.bedroom != null ? `${rec.bedroom}BR` : null,
    rec.areaSqft ? `${Math.round(Number(rec.areaSqft))} sqft` : null,
    rec.psf ? `$${Math.round(Number(rec.psf))} psf` : null,
    rec.floor != null ? `Floor ${rec.floor}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ ...cardSx, borderColor: 'var(--color-accent)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <EmojiEventsOutlinedIcon sx={{ fontSize: 18, color: 'var(--color-accent)' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            #{rec.rank} {rec.projectName} · {rec.unitNumber}
          </Typography>
        </Stack>
        <Chip label={`${rec.score}/100`} size="small" sx={{ fontWeight: 700 }} />
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{specs}</Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatPrice(rec.price)}
        </Typography>
      </Stack>
      {(rec.factors?.length ?? 0) > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
          {rec.factors!.slice(0, 4).join(' · ')}
        </Typography>
      )}
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        {rec.floorPlanUrl && (
          <Button size="small" startIcon={<ArchitectureOutlinedIcon />} sx={cardActionSx}
            onClick={() => onViewFloorPlan(rec.floorPlanUrl!, `${rec.projectName} ${rec.unitNumber} — Floor plan`)}>
            Floor plan
          </Button>
        )}
        {onOpenProject && rec.projectId && (
          <Button size="small" startIcon={<OpenInNewIcon />} sx={cardActionSx}
            onClick={() => onOpenProject(rec.projectId!)}>
            Open project
          </Button>
        )}
      </Stack>
    </Box>
  );
}

/** Full-width image dialog used for both floor plans and site plans. */
function ImageViewerDialog({ viewer, onClose }: { viewer: ImageViewerState | null; onClose: () => void }) {
  return (
    <Dialog open={viewer != null} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{viewer?.title}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ textAlign: 'center', minHeight: 200 }}>
        {viewer?.loading && <CircularProgress sx={{ my: 6 }} />}
        {viewer?.error && <Alert severity="info">{viewer.error}</Alert>}
        {viewer?.imageUrl && (
          <img
            src={viewer.imageUrl}
            alt={viewer.title}
            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Super Picker AI assistant chat dialog. Modeled on EmailAiEditorDialog:
 * stateless turn list, full visible history posted per turn, non-streaming
 * with a typing indicator (Firebase Functions buffers responses). Chat state
 * resets whenever the dialog closes (reset-per-open pilot behaviour); the
 * backend keeps the durable conversation log. Assistant turns can carry
 * structured results rendered as tappable project/unit/recommendation cards
 * with floor plan + site plan viewers and a deep link into the app.
 */
function SuperPickerChatDialog({
  open,
  onClose,
  onOpenProject,
}: {
  open: boolean;
  onClose: () => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<ImageViewerState | null>(null);
  const [exporting, setExporting] = useState(false);
  // Index of the turn whose 👎 reason panel is open, plus its comment draft.
  const [downPanelFor, setDownPanelFor] = useState<number | null>(null);
  const [downComment, setDownComment] = useState('');
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  // Push-to-talk recording + transcription state.
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  // Index of the turn currently being read aloud via speechSynthesis.
  const [speakingIdx, setSpeakingIdx] = useState<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const micSupported =
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia);
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const stopRecordingInternals = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    setRecording(false);
    setRecordSeconds(0);
  };

  const stopSpeaking = () => {
    if (ttsSupported) window.speechSynthesis.cancel();
    setSpeakingIdx(null);
  };

  // Reset chat state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setTurns([]);
      setConversationId(null);
      setPrompt('');
      setError(null);
      setLoading(false);
      setViewer(null);
      setExporting(false);
      setDownPanelFor(null);
      setDownComment('');
      setFeedbackBusy(false);
      setTranscribing(false);
      stopRecordingInternals();
      stopSpeaking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Release the mic and stop speech if the component unmounts mid-action.
  useEffect(() => () => {
    stopRecordingInternals();
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Auto-scroll the chat column to the latest turn.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, loading]);

  const handleSend = async () => {
    const text = prompt.trim();
    if (!text || loading) return;
    setError(null);

    // History sent to the backend = everything BEFORE the current prompt.
    const history: SuperPickerChatHistoryTurn[] = turns.map((t) => ({
      role: t.role,
      content: t.content,
    }));

    setTurns((prev) => [...prev, { role: 'user', content: text }]);
    setPrompt('');
    setLoading(true);

    try {
      const result = await sendSuperPickerChatMessage({
        message: text,
        conversationId,
        history,
      });
      setConversationId(result.conversationId);
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.reply,
          messageId: result.messageId,
          toolSummary: result.toolSummary,
          results: result.results,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chat request failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewFloorPlan = (url: string, title: string) => {
    setViewer({ title, imageUrl: url, loading: false, error: null });
  };

  const handleViewSitePlan = async (projectId: string, projectName: string) => {
    const title = `${projectName} — Site plan`;
    setViewer({ title, imageUrl: null, loading: true, error: null });
    try {
      const sitePlan = await api.getSitePlan(projectId);
      if (sitePlan?.imageUrl) {
        setViewer({ title, imageUrl: sitePlan.imageUrl, loading: false, error: null });
      } else {
        setViewer({ title, imageUrl: null, loading: false, error: 'No site plan is available for this project yet.' });
      }
    } catch (err) {
      setViewer({
        title,
        imageUrl: null,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load the site plan.',
      });
    }
  };

  const handleOpenProject = (projectId: string) => {
    if (!onOpenProject) return;
    onClose();
    onOpenProject(projectId);
  };

  const submitFeedback = async (
    turnIdx: number,
    feedback: 'up' | 'down',
    reason?: string,
    comment?: string,
  ) => {
    const turn = turns[turnIdx];
    if (!turn?.messageId || feedbackBusy) return;
    setFeedbackBusy(true);
    setError(null);
    try {
      await sendSuperPickerChatFeedback({
        messageId: turn.messageId,
        feedback,
        reason,
        comment,
      });
      setTurns((prev) => prev.map((t, i) => (i === turnIdx ? { ...t, feedback } : t)));
      // GA4 convenience copy — the DB row is the triage source of truth.
      logSuperPickerChatFeedback({
        feedback,
        reason: reason ?? null,
        conversationId,
        messageId: turn.messageId,
      });
      setDownPanelFor(null);
      setDownComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record feedback.');
    } finally {
      setFeedbackBusy(false);
    }
  };

  const renderFeedback = (turn: ChatTurn, turnIdx: number) => {
    if (!turn.messageId) return null;
    return (
      <Box sx={{ mt: 0.5 }}>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
            Helpful?
          </Typography>
          <IconButton
            size="small"
            aria-label="Thumbs up"
            disabled={feedbackBusy}
            onClick={() => { void submitFeedback(turnIdx, 'up'); }}
          >
            {turn.feedback === 'up'
              ? <ThumbUpIcon sx={{ fontSize: 16, color: 'var(--color-accent)' }} />
              : <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />}
          </IconButton>
          <IconButton
            size="small"
            aria-label="Thumbs down"
            disabled={feedbackBusy}
            onClick={() => {
              setDownPanelFor(downPanelFor === turnIdx ? null : turnIdx);
              setDownComment('');
            }}
          >
            {turn.feedback === 'down'
              ? <ThumbDownIcon sx={{ fontSize: 16, color: 'var(--color-accent)' }} />
              : <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />}
          </IconButton>
          {ttsSupported && (
            <Tooltip title={speakingIdx === turnIdx ? 'Stop reading' : 'Read aloud'}>
              <IconButton
                size="small"
                aria-label={speakingIdx === turnIdx ? 'Stop reading aloud' : 'Read reply aloud'}
                onClick={() => handleSpeak(turnIdx, turn.content)}
              >
                {speakingIdx === turnIdx
                  ? <VolumeOffIcon sx={{ fontSize: 16, color: 'var(--color-accent)' }} />
                  : <VolumeUpOutlinedIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {downPanelFor === turnIdx && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
              What went wrong? (one tap)
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {DOWN_REASONS.map((reason) => (
                <Chip
                  key={reason}
                  label={reason}
                  size="small"
                  variant="outlined"
                  disabled={feedbackBusy}
                  onClick={() => { void submitFeedback(turnIdx, 'down', reason, downComment.trim() || undefined); }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
              <TextField
                placeholder="Optional comment…"
                value={downComment}
                onChange={(e) => setDownComment(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: 280 }}
              />
              <Button
                size="small"
                disabled={feedbackBusy}
                sx={cardActionSx}
                onClick={() => { void submitFeedback(turnIdx, 'down', undefined, downComment.trim() || undefined); }}
              >
                Send
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    );
  };

  const handleStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop(); // onstop handles transcription + cleanup
    } else {
      stopRecordingInternals();
    }
  };

  const handleStartRecording = async () => {
    if (recording || transcribing || loading) return;
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone access was blocked — allow it in your browser settings to use voice input.');
      return;
    }

    const mimeType = pickRecordingMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      setError('Voice recording is not supported in this browser.');
      return;
    }

    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = async () => {
      const containerType = recorder.mimeType || mimeType || 'audio/webm';
      stopRecordingInternals();
      const blob = new Blob(chunks, { type: containerType });
      if (blob.size === 0) return;
      setTranscribing(true);
      try {
        const dataUrl = await blobToDataUrl(blob);
        const text = await transcribeSuperPickerChatAudio(dataUrl);
        if (text) {
          // Into the input box for confirmation — never auto-send.
          setPrompt((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Transcription failed.');
      } finally {
        setTranscribing(false);
      }
    };

    mediaRecorderRef.current = recorder;
    mediaStreamRef.current = stream;
    setRecording(true);
    setRecordSeconds(0);
    recordTimerRef.current = setInterval(() => {
      setRecordSeconds((secs) => {
        if (secs + 1 >= MAX_RECORDING_SECONDS) {
          handleStopRecording();
        }
        return secs + 1;
      });
    }, 1000);
    recorder.start();
  };

  const handleSpeak = (turnIdx: number, content: string) => {
    if (!ttsSupported) return;
    if (speakingIdx === turnIdx) {
      stopSpeaking();
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(toSpeakableText(content));
    // Default voice mangles Mandarin replies — hint the language for CJK text.
    if (/[一-鿿]/.test(content)) {
      utterance.lang = 'zh-CN';
    }
    utterance.onend = () => setSpeakingIdx(null);
    utterance.onerror = () => setSpeakingIdx(null);
    setSpeakingIdx(turnIdx);
    window.speechSynthesis.speak(utterance);
  };

  const handleExportPdf = async () => {
    if (!conversationId || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const blob = await downloadSuperPickerChatReport(conversationId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `super-picker-ai-report-${conversationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Report export failed.');
    } finally {
      setExporting(false);
    }
  };

  const renderResults = (results?: SuperPickerChatResults) => {
    if (!results) return null;
    const { recommendations, projects, units } = results;
    return (
      <Stack spacing={0.75} sx={{ mt: 0.75, width: '100%' }}>
        {recommendations?.map((rec) => (
          <RecommendationCard
            key={`rec-${rec.unitId}`}
            rec={rec}
            onViewFloorPlan={handleViewFloorPlan}
            onOpenProject={onOpenProject ? handleOpenProject : undefined}
          />
        ))}
        {projects?.map((project) => (
          <ProjectCard
            key={`proj-${project.projectId}`}
            project={project}
            onViewSitePlan={handleViewSitePlan}
            onOpenProject={onOpenProject ? handleOpenProject : undefined}
          />
        ))}
        {units?.map((unit) => (
          <UnitCard
            key={`unit-${unit.unitId}`}
            unit={unit}
            onViewFloorPlan={handleViewFloorPlan}
            onOpenProject={onOpenProject ? handleOpenProject : undefined}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: 'var(--color-accent)' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Super Picker Assistant
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Prices are indicative — always verify with the Project IC before committing.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={conversationId ? 'Export PDF report' : 'Send a message first'}>
            <span>
              <IconButton
                onClick={handleExportPdf}
                disabled={!conversationId || exporting || loading}
                size="small"
              >
                {exporting ? <CircularProgress size={18} /> : <PictureAsPdfOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minHeight: 320,
            maxHeight: { xs: '55vh', md: 460 },
          }}
        >
          {turns.length === 0 && (
            <Box
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                fontSize: 14,
                py: 3,
                textAlign: 'center',
              }}
            >
              Ask me about new launches. Examples:
              <br />
              <em>"Any new launch near a primary school in D19 under $2M?"</em>
              <br />
              <em>"Recommend the best 3-bedder for $2.5M — schools matter most."</em>
            </Box>
          )}
          {turns.map((turn, idx) => (
            <Box
              key={`${turn.role}-${idx}`}
              sx={{
                alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: turn.role === 'user' ? '88%' : '94%',
                bgcolor: turn.role === 'user' ? 'var(--color-accent)' : 'action.hover',
                color: turn.role === 'user' ? '#fff' : 'text.primary',
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ display: 'block', fontWeight: 700, opacity: 0.8, mb: 0.25 }}
              >
                {turn.role === 'user' ? 'You' : 'Assistant'}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {turn.content}
              </Typography>
              {turn.role === 'assistant' && renderResults(turn.results)}
              {turn.role === 'assistant' && (turn.toolSummary?.length ?? 0) > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                  {turn.toolSummary!.map((label) => (
                    <Chip key={label} label={label} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}
              {turn.role === 'assistant' && renderFeedback(turn, idx)}
            </Box>
          ))}
          {loading && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
            >
              <CircularProgress size={14} />
              <Typography variant="body2">Checking live data…</Typography>
            </Stack>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
          }}
        >
          <TextField
            placeholder={recording ? 'Listening…' : transcribing ? 'Transcribing…' : 'Ask about new launches…'}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            multiline
            maxRows={4}
            fullWidth
            disabled={loading}
            size="small"
            inputProps={{ maxLength: 2000 }}
          />
          {micSupported && (
            <Tooltip title={recording ? 'Stop recording' : 'Speak your question'}>
              <span>
                <IconButton
                  aria-label={recording ? 'Stop recording' : 'Record a voice question'}
                  disabled={loading || transcribing}
                  onClick={() => { recording ? handleStopRecording() : void handleStartRecording(); }}
                  sx={{
                    alignSelf: 'center',
                    ...(recording ? { color: '#d32f2f' } : { color: 'var(--color-accent)' }),
                  }}
                >
                  {transcribing
                    ? <CircularProgress size={20} />
                    : recording
                      ? <StopIcon />
                      : <MicIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {recording && (
            <Typography variant="caption" sx={{ alignSelf: 'center', color: '#d32f2f', minWidth: 32 }}>
              {recordSeconds}s
            </Typography>
          )}
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={loading || prompt.trim().length === 0}
            startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon />}
            sx={{
              textTransform: 'none',
              bgcolor: 'var(--color-accent)',
              '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(0.95)' },
            }}
          >
            Send
          </Button>
        </Box>
      </DialogContent>

      <ImageViewerDialog viewer={viewer} onClose={() => setViewer(null)} />
    </Dialog>
  );
}

interface SuperPickerChatFabProps {
  /** Deep link into the project detail page (e.g. handleMapResearchViewProject). */
  onOpenProject?: (projectId: string) => void;
  /** Zero-based position in the compact bottom-right action stack. */
  stackIndex?: number;
}

/**
 * Floating action button that opens the Super Picker AI assistant. Anchored in
 * the bottom-right FAB column and positioned from its assigned stack index so
 * it stays above any visible compare and share actions.
 */
export const SuperPickerChatFab: React.FC<SuperPickerChatFabProps> = ({ onOpenProject, stackIndex = 0 }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Super Picker Assistant" placement="left">
        <Fab
          aria-label="Super Picker Assistant"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            // Reserve one FAB slot for the host app's bottom-right +/- control
            // on mobile and tablet. Desktop keeps the standard page inset.
            bottom: { xs: 56 + (stackIndex * 68), md: 24 + (stackIndex * 68) },
            right: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.speedDial,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(0.95)' },
          }}
        >
          <AutoAwesomeIcon />
        </Fab>
      </Tooltip>
      <SuperPickerChatDialog
        open={open}
        onClose={() => setOpen(false)}
        onOpenProject={onOpenProject}
      />
    </>
  );
};

export default SuperPickerChatFab;
